import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ==================== Depreciation ====================

export type TriggerSource = 'MANUAL' | 'AUTO_MONTH_CLOSE';
export type DepreciationAssetKind = 'FIXED_ASSET' | 'INTANGIBLE';
export type AllowedStatusChange = 'OPERATING' | 'CONSERVATION' | 'REPAIR_LONG';

export interface DepreciationCalculationItem {
  id: string;
  line_number: number;
  asset_kind: DepreciationAssetKind;
  fixed_asset_id: string | null;
  intangible_asset_id: string | null;
  primary_cost_snapshot: string;
  liquidation_value_snapshot: string;
  accumulated_before: string;
  useful_life_months_snapshot: number;
  method_code_snapshot: string;
  amount: string;
  expense_account_code: string;
  depreciation_account_code: string;
}

export interface DepreciationCalculation {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_month: number;
  trigger_source: TriggerSource;
  total_amount: string;
  items_count: number;
  skipped_count: number;
  skip_reasons: Record<string, number> | null;
  external_source?: string | null;
  items: DepreciationCalculationItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface CalculateForPeriodRequest {
  organization_id: string;
  period_year: number;
  period_month: number;
  force_recreate?: boolean;
}

export interface CalculateForPeriodResponse {
  document_id: string;
  document_number: string;
  items_count: number;
  skipped_count: number;
  total_amount: string;
  was_recreated: boolean;
  skip_reasons: Record<string, number>;
}

const BASE = '/documents/depreciation-calculation';

export const depreciationCalculation = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_month?: number;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<DepreciationCalculation>>(BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DepreciationCalculation>(`${BASE}/${id}`).then((r) => r.data),
  calculateForPeriod: (p: CalculateForPeriodRequest) =>
    api
      .post<CalculateForPeriodResponse>(`${BASE}/calculate-for-period`, p)
      .then((r) => r.data),
  unpost: (id: string) =>
    api.post<DepreciationCalculation>(`${BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  remove: (id: string) => api.delete(`${BASE}/${id}`).then(() => undefined),
};

// ==================== Asset Status Change ====================

export interface AssetStatusChangeRequest {
  new_status: AllowedStatusChange;
  reason: string;
  effective_date: string;
}

export const assetStatus = {
  changeFixedAsset: (id: string, p: AssetStatusChangeRequest) =>
    api.patch(`/refs/fixed-assets/${id}/status`, p).then((r) => r.data),
  changeIntangibleAsset: (id: string, p: AssetStatusChangeRequest) =>
    api.patch(`/refs/intangible-assets/${id}/status`, p).then((r) => r.data),
};
